/**
 * @lodex-pro/web-admin/features/wells/hooks — Task 25.4 (DEC-115).
 *
 * TanStack Query hooks over the PLATFORM_ADMIN wells-management API
 * (/v1/platform/wells): paginated list/search, detail, manual add, edit, and
 * deactivate/reactivate.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts, type EmptyBody } from '@lodex-pro/web-shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WellSummary {
  readonly id: string;
  readonly apiNumber: string;
  readonly wellName: string;
  readonly operatorId: string | null;
  readonly operatorName: string | null;
  readonly state: string;
  readonly county: string | null;
  readonly wellType: string | null;
  readonly wellStatus: string | null;
  readonly wellheadLat: string | null;
  readonly wellheadLng: string | null;
  readonly isActive: boolean;
  readonly source: string | null;
  readonly haulerCount: number;
}

/** How a hauler is linked to the well (OPEN-149): its pickup catalog, or an authorized disposal well. */
export type WellLinkKind = 'PICKUP' | 'DISPOSAL';

export interface WellHaulerLink {
  readonly kind: WellLinkKind;
  /** ISO; when the hauler added the pickup well / authorized the disposal well. */
  readonly linkedAt: string | null;
}

export interface WellHauler {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly hasTanks: boolean;
  readonly inBattery: boolean;
  readonly links: readonly WellHaulerLink[];
}

export interface WellBattery {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly batteryName: string;
}

export interface WellDetail {
  readonly well: WellSummary;
  readonly haulers: readonly WellHauler[];
  readonly tankBatteries: readonly WellBattery[];
}

export interface WellFilters {
  readonly q?: string;
  readonly state?: string;
  readonly county?: string;
  readonly wellType?: string;
  readonly wellStatus?: string;
  readonly isActive?: boolean;
  readonly operatorId?: string;
  readonly source?: string;
  readonly hasHaulerImports?: boolean;
  readonly sort?: 'wellName' | 'apiNumber' | 'county' | 'haulerCount';
}

export const PAGE_SIZE = 50;

// ─── Query keys ──────────────────────────────────────────────────────────────

export const WELLS_KEY = ['platform', 'wells'] as const;
export const WELL_KEY = (id: string): readonly string[] => ['platform', 'wells', id];

// ─── Queries ─────────────────────────────────────────────────────────────────

function toParams(f: WellFilters, offset: number): string {
  const p = new URLSearchParams();
  if (f.q !== undefined && f.q !== '') p.set('q', f.q);
  if (f.state !== undefined && f.state !== '') p.set('state', f.state);
  if (f.county !== undefined && f.county !== '') p.set('county', f.county);
  if (f.wellType !== undefined && f.wellType !== '') p.set('wellType', f.wellType);
  if (f.wellStatus !== undefined && f.wellStatus !== '') p.set('wellStatus', f.wellStatus);
  if (f.isActive !== undefined) p.set('isActive', String(f.isActive));
  if (f.operatorId !== undefined && f.operatorId !== '') p.set('operatorId', f.operatorId);
  if (f.source !== undefined && f.source !== '') p.set('source', f.source);
  if (f.hasHaulerImports !== undefined) p.set('hasHaulerImports', String(f.hasHaulerImports));
  p.set('sort', f.sort ?? 'wellName');
  p.set('limit', String(PAGE_SIZE));
  p.set('offset', String(offset));
  return p.toString();
}

export function useWells(
  filters: WellFilters,
  offset: number,
): ReturnType<
  typeof useApiQuery<{
    wells: readonly WellSummary[];
    total: number;
    limit: number;
    offset: number;
  }>
> {
  const opts = useApiOpts();
  const params = toParams(filters, offset);
  return useApiQuery<{
    wells: readonly WellSummary[];
    total: number;
    limit: number;
    offset: number;
  }>(opts, `/v1/platform/wells?${params}`, { queryKey: [...WELLS_KEY, params], staleTime: 15_000 });
}

export function useWell(id: string): ReturnType<typeof useApiQuery<WellDetail>> {
  const opts = useApiOpts();
  return useApiQuery<WellDetail>(opts, `/v1/platform/wells/${id}`, {
    queryKey: WELL_KEY(id),
    staleTime: 15_000,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export interface CreateWellBody {
  apiNumber: string;
  wellName: string;
  operatorId: string;
  state: string;
  county?: string;
  wellType?: string;
  wellStatus?: string;
  wellheadLat?: number;
  wellheadLng?: number;
  source?: string;
}

export function useCreateWell(
  onDone: (id: string) => void,
): ReturnType<typeof useApiMutation<CreateWellBody, { well: WellSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateWellBody, { well: WellSummary }>(opts, '/v1/platform/wells', 'POST', {
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: WELLS_KEY });
      onDone(res.well.id);
    },
  });
}

export interface UpdateWellBody {
  wellName?: string;
  operatorId?: string | null;
  wellStatus?: string | null;
  wellheadLat?: number | null;
  wellheadLng?: number | null;
}

export function useUpdateWell(
  id: string,
): ReturnType<typeof useApiMutation<UpdateWellBody, { well: WellSummary }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<UpdateWellBody, { well: WellSummary }>(
    opts,
    `/v1/platform/wells/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: WELL_KEY(id) });
        void qc.invalidateQueries({ queryKey: WELLS_KEY });
      },
    },
  );
}

function useWellActive(
  id: string,
  action: 'deactivate' | 'reactivate',
): ReturnType<typeof useApiMutation<EmptyBody, { well: { id: string; isActive: boolean } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<EmptyBody, { well: { id: string; isActive: boolean } }>(
    opts,
    `/v1/platform/wells/${id}/${action}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: WELL_KEY(id) });
        void qc.invalidateQueries({ queryKey: WELLS_KEY });
      },
    },
  );
}

export function useDeactivateWell(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { well: { id: string; isActive: boolean } }>> {
  return useWellActive(id, 'deactivate');
}

export function useReactivateWell(
  id: string,
): ReturnType<typeof useApiMutation<EmptyBody, { well: { id: string; isActive: boolean } }>> {
  return useWellActive(id, 'reactivate');
}
